import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  DiskspaceRespondDto,
  LicenseChartRequestDto,
  TopDocWorkgroupDto,
  TopMemberWorkgroupDto,
} from "../../../types/adminHome";
import { EcoSystemLicenseStats } from "../../../types/adminConfig";
import adminHomeApi from "../../api/admin/AdminHome";
import FileSaver from "file-saver";
import dayjs from "dayjs";

// # initial state
const initialState = {
  diskspace: {
    freeSize: 0,
    totalSize: 0,
    usageRate: 0,
    usageSize: 0,
  } as DiskspaceRespondDto,
  createdWorkgroup: {
    today: 0,
    total: 0,
  },
  sharedDoc: {
    today: 0,
    total: 0,
  },
  license: {
    externalLicenseCount: 0,
    internalLicenseCount: 0,
    usedExternalCount: 0,
    usedInternalCount: 0,
    allowMobile: false,
    allowMacOS: false,
    allowWebView: false,
  } as EcoSystemLicenseStats,
  member: {
    today: 0,
    total: 0,
  },
  linechart: {
    docStatsMonthly: [] as {
      logDate: string;
      count: number;
    }[],
    memberStatsMonthly: [] as {
      logDate: string;
      count: number;
    }[],
    workgroupStatsMonthly: [] as {
      logDate: string;
      count: number;
    }[],
  },
  topMemberWorkgroup: [] as TopMemberWorkgroupDto[],
  topDocWorkgroup: [] as TopDocWorkgroupDto[],
};

export const trunkGetDiskSpace = createAsyncThunk(
  "wapi/admins/dashboard/diskspace",
  async () => {
    const resp = await adminHomeApi.getdiskSpace();
    return resp;
  }
);
export const trunkGetCreatedWorkgroupCnt = createAsyncThunk(
  "wapi/admins/dashboard/workgroup",
  async () => {
    const resp = await adminHomeApi.getCreatedWorkgroupCount();
    return resp;
  }
);
export const trunkGetSharedDocCnt = createAsyncThunk(
  "wapi/admins/dashboard/share/doc",
  async () => {
    const resp = await adminHomeApi.getSharedDocCount();
    return resp;
  }
);
export const trunkGetPageLicense = createAsyncThunk(
  "wapi/admins/dashboard/license",
  async () => {
    const resp = await adminHomeApi.getLicenseInfo();
    return resp;
  }
);
export const trunkGetMemberCnt = createAsyncThunk(
  "wapi/admins/dashboard/member",
  async () => {
    const resp = await adminHomeApi.getMemberCnt();
    return resp;
  }
);
export const trunkGetLineChart = createAsyncThunk(
  "wapi/admins/dashboard/linechart",
  async (data: LicenseChartRequestDto) => {
    const resp = await adminHomeApi.getLineChart(data);
    return resp;
  }
);
export const trunkGetTopMemberWorkgroup = createAsyncThunk(
  "wapi/admins/dashboard/top/workgroup",
  async () => {
    const resp = await adminHomeApi.getTopMemberWorkgroup();
    return resp;
  }
);
export const trunkGetTopDoc = createAsyncThunk(
  "wapi/admins/dashboard/top/doc",
  async () => {
    const resp = await adminHomeApi.getTopDoc();
    return resp;
  }
);
export const trunkDownloadChart = createAsyncThunk(
  "wapi/admins/dashboard/chart/excel",
  async (data: LicenseChartRequestDto) => {
    const resp = await adminHomeApi.downloadChart(data);
    return resp;
  }
);

const adminHomeSlice = createSlice({
  name: "adminHome",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder.addCase(
      trunkGetDiskSpace.fulfilled,
      (state, { payload: newDiskInfo }) => {
        state.diskspace.freeSize = newDiskInfo.freeSize;
        state.diskspace.totalSize = newDiskInfo.totalSize;
        state.diskspace.usageRate = newDiskInfo.usageRate;
        state.diskspace.usageSize = newDiskInfo.usageSize;
      }
    );
    builder.addCase(
      trunkGetCreatedWorkgroupCnt.fulfilled,
      (state, { payload: newInfo }) => {
        state.createdWorkgroup.today = newInfo.today;
        state.createdWorkgroup.total = newInfo.total;
      }
    );
    builder.addCase(
      trunkGetSharedDocCnt.fulfilled,
      (state, { payload: newInfo }) => {
        state.sharedDoc.today = newInfo.today;
        state.sharedDoc.total = newInfo.total;
      }
    );
    builder.addCase(
      trunkGetPageLicense.fulfilled,
      (state, { payload: newInfo }) => {
        state.license = newInfo;
      }
    );
    builder.addCase(
      trunkGetMemberCnt.fulfilled,
      (state, { payload: newInfo }) => {
        state.member = newInfo;
      }
    );
    builder.addCase(
      trunkGetLineChart.fulfilled,
      (state, { payload: newInfo }) => {
        state.linechart.workgroupStatsMonthly = [];
        state.linechart.memberStatsMonthly = [];
        state.linechart.docStatsMonthly = [];

        newInfo.workgroupStatsMonthly.map((item) => {
          state.linechart.workgroupStatsMonthly.push(item);
        });
        newInfo.memberStatsMonthly.map((item) => {
          state.linechart.memberStatsMonthly.push(item);
        });
        newInfo.docStatsMonthly.map((item) => {
          state.linechart.docStatsMonthly.push(item);
        });
      }
    );
    builder.addCase(
      trunkGetTopMemberWorkgroup.fulfilled,
      (state, { payload: newInfo }) => {
        state.topMemberWorkgroup = newInfo;
      }
    );
    builder.addCase(trunkGetTopDoc.fulfilled, (state, { payload: newInfo }) => {
      state.topDocWorkgroup = newInfo;
    });

    builder.addCase(trunkDownloadChart.fulfilled, (state, { payload: res }) => {
      if (res.data && res.headers) {
        const file = res.data;
        const fileName = dayjs().format("YYYYMMDD") + "_statistics.xlsx";
        FileSaver.saveAs(file, fileName);
      }
    });
  },
});

export const adminHomeActions = adminHomeSlice.actions;
export default adminHomeSlice.reducer;
